import {
  cloneElement,
  isValidElement,
  useState,
  type ReactElement,
  type ReactNode,
  type Ref,
} from "react";
import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
  type Placement,
} from "@floating-ui/react";
import { cn } from "@/lib/utils";

/**
 * Tooltip — the definition overlay (ENG-APP-UX-TUNEUP Track D).
 *
 * Source: docs/product/experience-and-ui/48-in-app-design-spec.md § 2.3 —
 * ink surface, 14 px, max 300 px, 8 px radius; opens on hover OR focus, closes
 * on Esc and on leave; it DEFINES, it never holds an action; no native
 * `title=` anywhere (audit D1: 409 of them). Positioning, the interaction
 * state machine and the `aria-describedby` wiring come from `@floating-ui/react`
 * (MIT; adopted by Track D for every overlay — a hand-rolled overlay is the
 * classic accessibility failure, UI_COMPONENT_CONTRACT § Canonical primitive
 * set records the pick). The child MUST be a single focusable element (a
 * Button, a link, an icon button): a tooltip on something a keyboard cannot
 * reach is invisible to keyboard users.
 */
export interface TooltipProps {
  /** The definition. Text only — never an action. Empty (undefined / null /
   * false / "") renders the child alone, so a conditional definition keeps
   * its semantics without a wrapper branch (migration wave 3). */
  content?: ReactNode;
  /** One focusable element; it receives the trigger props. */
  children: ReactElement<{ ref?: Ref<HTMLElement> }>;
  placement?: Placement;
  /** Milliseconds before the tooltip opens on hover (focus opens at once). */
  delay?: number;
  className?: string;
}

export default function Tooltip({
  content,
  children,
  placement = "top",
  delay = 300,
  className,
}: TooltipProps) {
  const [open, setOpen] = useState(false);
  // Controlled elements (the pattern @floating-ui/react documents for React
  // 19 + the React Compiler lint): state setters as refs, no `refs` object
  // read during render.
  const [referenceEl, setReferenceEl] = useState<HTMLElement | null>(null);
  const [floatingEl, setFloatingEl] = useState<HTMLElement | null>(null);
  const { floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    elements: { reference: referenceEl, floating: floatingEl },
    middleware: [offset(6), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });
  const hover = useHover(context, {
    delay: { open: delay, close: 0 },
    move: false,
  });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ]);
  if (!isValidElement(children)) return children;
  if (
    content === undefined ||
    content === null ||
    content === false ||
    content === ""
  ) {
    return children;
  }
  const trigger = cloneElement(
    children,
    getReferenceProps({ ref: setReferenceEl, ...children.props }),
  );
  return (
    <>
      {trigger}
      {open && (
        <FloatingPortal>
          <div
            ref={setFloatingEl}
            style={floatingStyles}
            {...getFloatingProps()}
            className={cn(
              "z-50 max-w-[300px] rounded-lg bg-ink px-3 py-2 text-sm text-white shadow-lg",
              className,
            )}
          >
            {content}
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
